import { useEffect, useRef } from 'react';
import { cn } from '../styles/lib/utils';

export { cn };

export function useScrollToTopWithReturn(
  scrollContainerRef: React.RefObject<HTMLElement | null>,
  shouldScrollToTop: boolean,
) {
  const previousScrollPositionRef = useRef<number>(0);
  const hasSavedPositionRef = useRef<boolean>(false);

  useEffect(() => {
    if (!scrollContainerRef.current) return;

    if (shouldScrollToTop) {
      // Save current scroll position before scrolling to top
      previousScrollPositionRef.current = scrollContainerRef.current.scrollTop;
      hasSavedPositionRef.current = true;
      // Scroll to top
      scrollContainerRef.current.scrollTo({ top: 0 });
    } else {
      // Restore previous scroll position
      if (hasSavedPositionRef.current) {
        scrollContainerRef.current.scrollTo({
          top: previousScrollPositionRef.current,
        });
      }
    }
  }, [shouldScrollToTop, scrollContainerRef.current]);
}

export const numberFormatter = new Intl.NumberFormat('en-US');

export function clampValue(value: number | undefined, min = 0, max = 100): number {
  const lowerBound = Math.min(min, max);
  const upperBound = Math.max(min, max);

  if (value === undefined || Number.isNaN(value)) {
    return lowerBound;
  }

  if (value < lowerBound) {
    return lowerBound;
  }

  if (value > upperBound) {
    return upperBound;
  }

  return value;
}

export type ProgressState = 'pending' | 'in-progress' | 'complete';
export function resolveProgressState(value: number | undefined): ProgressState {
  if (value === undefined || value <= 0) {
    return 'pending';
  }
  if (value >= 100) {
    return 'complete';
  }
  return 'in-progress';
}

export type AvatarUser = {
  name: string;
  email: string;
  url?: string;
};

export type AvatarMember = {
  firstName: string;
  lastName: string;
  email: string;
  url?: string;
};

export function getAvatarLetters(user: AvatarUser | AvatarMember): string {
  if ('name' in user) {
    // Handle AvatarUser (name)
    if (!user.name || user.name === '' || user.name === 'null null') {
      return user.email.charAt(0);
    }

    const splits = user.name.split(' ');
    if (splits.length === 1) {
      return splits[0].charAt(0);
    } else {
      return `${splits[0].charAt(0)}${splits[splits.length - 1].charAt(0)}`;
    }
  }

  // Handle AvatarMember (firstName + lastName)
  if (!user.firstName && !user.lastName) {
    return user.email.charAt(0);
  }

  if (!user.lastName) {
    return user.firstName.charAt(0);
  }

  return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
}

// Temp function to handle null user names.
export function getNonNullUserName(name: string | undefined): string {
  if (name && name !== 'null null') {
    return name;
  }
  return '';
}

export * from './advancedFilterShortcuts';
export * from './canvas';
export * from './clientTableState';
export * from './filterSettings';
export * from './formatFileSize';
export * from './getTabbables';
export * from './mapLoadWatchdog';
export * from './statusHelpers';
export * from './tableSettings';
export * from './typeHelpers';
